import { type ClassValue, clsx } from "clsx";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getTimeAgo = (timestamp: any) => {
  const now = new Date();
  const givenTime = new Date(timestamp);
  //@ts-ignore
  const diff = (now - givenTime) / 1000; // Calculate difference in seconds

  if (diff < 0) {
    return formatDateFromTimestamp(timestamp);
  }

  if (diff < 60) {
    return `${Math.floor(diff)}s ago`;
  } else if (diff < 3600) {
    return `${Math.floor(diff / 60)}m ago`;
  } else if (diff < 86400) {
    return `${Math.floor(diff / 3600)}h ago`;
  } else {
    return `${Math.floor(diff / 86400)}d ago`;
  }
};

export function formatDateFromTimestamp(timestamp: any) {
  // Create a new Date object using the timestamp
  const date = new Date(timestamp);

  // Get the components of the date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Create a formatted date string
  const formattedDate = `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;

  return formattedDate;
}

export const getStatusColor = (status: any) => {
  switch (status) {
    case "order_accepted":
      return "success";
    case "order_start_trip":
      return "secondary";
    case "order_end_job":
      return "info";
    case "order_pending":
      return "warning";
    case "order_cancel":
      return "danger";
    // Add more cases for other status values as needed
    default:
      return "info"; // Default color
  }
};

const formatDate = (date: any) => {
  if (date) {
    const dt = new Date(date);
    const month =
      dt.getMonth() + 1 < 10 ? "0" + (dt.getMonth() + 1) : dt.getMonth() + 1;
    const day = dt.getDate() < 10 ? "0" + dt.getDate() : dt.getDate();
    return day + "/" + month + "/" + dt.getFullYear();
  }
  return "";
};

export const moneyFormatter = (number: number) => {
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });

  const amount = Math.round(number);
  return formatter.format(amount);
};

export function getValueAfterDotCom(url: any) {
  if (url === "" || !url) {
    return "Not Available";
  }
  const match = url?.match(/\.com\/(.*)/);

  if (match && match[1]) {
    return match[1];
  } else {
    // Return the original URL if ".com/" is not found
    return "Link";
  }
}

export const getCurrentLocation = async () => {
  try {
    const position = await new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position);
          },
          (error) => {
            reject(error);
          }
        );
      } else {
        reject(new Error("Geolocation is not supported by this browser."));
      }
    });
    // @ts-ignore
    const { latitude, longitude } = position.coords;
    const latlng = { lat: latitude, lng: longitude };
    console.log(latlng);
    return latlng;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const geocodeLatLng = (geocoder: any, latlng: any) => {
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (results: any, status: any) => {
      if (status === "OK") {
        resolve(results);
      } else {
        reject(new Error(`Geocode failed with status ${status}`));
      }
    });
  });
};

export function xanoformatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export const formatDateIntoText = (date: any) => {
  const options = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  };
  return date.toLocaleDateString("en-GB", options);
};

export const calucluateRent = ({
  base_rent,
  per_day_rent,
  per_trip_rent,
  nFactor,
  mainSurge,
  number_of_days,
  same_day_surge,
  city_surge,
  product_surge,
}: {
  base_rent: number;
  per_day_rent: number;
  per_trip_rent: number;
  nFactor: number;
  mainSurge: number;
  city_surge: number;
  same_day_surge: number;
  number_of_days: number;
  product_surge: number;
}) => {
  const num_days = number_of_days;
  const finalRent =
    base_rent +
    per_day_rent * Math.min(num_days, 15) +
    per_trip_rent +
    Math.max(num_days - 15, 0) * nFactor;

  let newSurge =
    mainSurge * (city_surge ?? 1) * (same_day_surge ?? 1) * (product_surge ?? 1);
  if (newSurge < 0.75) newSurge = 0.75;
  if (newSurge > 1.25) newSurge = 1.25;
  return (finalRent * newSurge)?.toFixed();
};

export const areDatesEqual = (date1: Date, date2: Date) => {
  date1.setHours(0, 0, 0, 0);
  date2.setHours(0, 0, 0, 0);

  return date1.getTime() === date2.getTime();
};

// URL PARAMETER UTILITIES FOR DASHBOARD

// URL Parameter interface
export interface UrlParams {
  calling_number?: string;
  country_code_calling?: string;
  user_id?: string;
  order_id?: string;
  phone_number?: string;
  ticket_id?: string;
  ticket_type?: string;
}

export const getDigitsOnly = (value: string | undefined | null): string =>
  (value ?? "").replace(/\D/g, "");

export const isValidLookupPhone = (value: string | undefined | null): boolean =>
  getDigitsOnly(value).length === 10;

export const isValidOrderId = (value: string | undefined | null): boolean =>
  (value ?? "").trim().length > 0;

// Hook to read URL parameters

export const useUrlParams = (): UrlParams => {
  const [searchParams] = useSearchParams();

  return useMemo(
    () => ({
      calling_number: searchParams.get("calling_number") || undefined,
      country_code_calling:
        searchParams.get("country_code_calling") || undefined,
      user_id: searchParams.get("user_id") || undefined,
      order_id: searchParams.get("order_id") || undefined,
      phone_number: searchParams.get("phone_number") || undefined,
      ticket_id: searchParams.get("ticket_id") || undefined,
      ticket_type: searchParams.get("ticket_type") || undefined,
    }),
    [searchParams]
  );
};

// Utility function to convert snake_case to Title Case
export const snakeToTitleCase = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Utility function to check if a string is a URL
export const isUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

// Utility function to check if a string is an image URL
export const isImageUrl = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  const lowerStr = str.toLowerCase();
  return imageExtensions.some(ext => lowerStr.includes(ext)) || isUrl(str);
};
